(() => {
  const CONFIG = {
    apiBase: 'https://monogo.malbi.com.ua/api',
    endpoints: { init: '/checkout/init' },
    amountMode: 'units',
    checkoutSelector: '',
    // checkoutSelector: '[data-external-checkout], #PayMonoCheckout, #CartDrawer-Checkout',
    credentials: 'include',
  };

  class Money {
    constructor(shopifyFormat) {
      this.shopifyFormat = shopifyFormat || null;
    }

    formatCents(cents) {
      try {
        if (window.Shopify && typeof Shopify.formatMoney === 'function') {
          return this.shopifyFormat
              ? Shopify.formatMoney(cents, this.shopifyFormat)
              : Shopify.formatMoney(cents);
        }
      } catch {}
      return (cents / 100).toFixed(2);
    }

    centsToUnits(cents) {
      return Number(((cents || 0) / 100).toFixed(2));
    }
  }

  class Utils {
    static normalizeImageUrl(url) {
      if (!url) return '';
      return url.startsWith('//') ? 'https:' + url : url;
    }

    static toVariantGid(id) {
      const s = String(id);
      return s.startsWith('gid://')
          ? s
          : `gid://shopify/ProductVariant/${s}`;
    }

    static generateUUID() {
      return crypto.randomUUID();
    }

    static compact(obj) {
      const out = {};
      for (const [k, v] of Object.entries(obj)) {
        if (v !== undefined && v !== null) out[k] = v;
      }
      return out;
    }

    static normalizePhone(value) {
      if (value == null) return '';
      return String(value).replace(/\D+/g, '');
    }

    static formatPhoneE164(value) {
      const raw = (value == null ? '' : String(value));
      const digits = raw.replace(/\D+/g, '');
      let nsn = digits;
      if (nsn.startsWith('380')) {
        nsn = nsn.slice(3);
      } else if (nsn.startsWith('80')) {
        nsn = nsn.slice(2);
      } else if (nsn.startsWith('0')) {
        nsn = nsn.slice(1);
      } else if (nsn.startsWith('8')) {
        nsn = nsn.slice(1);
      }
      if (nsn.length > 9) nsn = nsn.slice(-9);
      return `+380${nsn}`;
    }

    static isValidPhone(value) {
      const raw = (value == null ? '' : String(value)).trim().replace(/\s+/g, '');
      let required = 10;
      if (raw.startsWith('+38')) {
        required = 13;
      } else if (raw.startsWith('38')) {
        required = 12;
      } else if (raw.startsWith('8')) {
        required = 11;
      }

      const shapeOk = /^\+?\d+$/.test(raw);
      return shapeOk && raw.length >= required;
    }
  }

  class CartItem {
    constructor({ variantId, quantity, title, imgURL, priceCentsEach, lineCents, sku, properties }) {
      this.variantId = variantId;
      this.quantity = quantity;
      this.title = title;
      this.imgURL = imgURL;
      this.priceCentsEach = priceCentsEach;
      this.lineCents = lineCents;
      this.sku = (sku ?? '').toString().trim();
      this.properties = properties ? properties : {};
    }

    static fromRaw(item) {
      return new CartItem({
        variantId: item.variant_id ?? item.id,
        quantity: item.quantity,
        title: item.title,
        imgURL: Utils.normalizeImageUrl(item.image || item?.featured_image?.url || ''),
        priceCentsEach: item.price ?? 0,
        lineCents: item.final_line_price ?? item.line_price ?? 0,
        sku: item.sku ?? '',
        properties: item.properties || {},
      });
    }
  }

  class Cart {
    constructor(items = [], raw = {}) {
      this.items = items;
      this.raw = raw;
      this.totalQuantity = items.reduce((sum, i) => sum + (i.quantity || 0), 0);
      this.totalPriceCents = typeof raw.total_price === 'number'
          ? raw.total_price
          : items.reduce((sum, i) => sum + (i.lineCents || 0), 0);
    }

    static fromApi(raw) {
      const items = (raw.items || []).map(CartItem.fromRaw);
      return new Cart(items, raw);
    }
  }

  class CartApi {
    constructor() {
      this.root = (window.Shopify?.routes?.root) || '/';
    }

    async get() {
      const res = await fetch(this.root + 'cart.js', { credentials: 'include' });
      return res.json();
    }

    async clear() {
      try {
        await fetch(this.root + 'cart/clear.js', { method: 'POST', credentials: 'include' });
      } catch {}
    }
  }

  class ApiClient {
    constructor(baseUrl, credentials = 'include') {
      this.baseUrl = baseUrl.replace(/\/+$/, '');
      this.credentials = credentials;
    }

    async postJSON(path, body) {
      const res = await fetch(`${this.baseUrl}${path}`, {
        method: 'POST',
        credentials: this.credentials,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        throw new Error(`Request failed ${res.status}: ${await res.text().catch(() => res.statusText)}`);
      }

      return res.json();
    }
  }

  class CheckoutService {
    constructor({ money, api, endpoints, amountMode = 'units', cartApi }) {
      this.money = money;
      this.api = api;
      this.endpoints = endpoints;
      this.amountMode = amountMode;
      this.cartApi = cartApi;
    }

    async parseCart() {
      const raw = await this.cartApi.get();
      return Cart.fromApi(raw);
    }

    buildLineItems(cart) {
      return cart.items.map(i => {
        const props = i.properties && typeof i.properties === "object"
            ? Object.entries(i.properties)
                .filter(([k, v]) => k && v)
                .map(([name, value]) => ({ name, value }))
            : [];

        return {
          variantId: Utils.toVariantGid(i.variantId),
          quantity: i.quantity,
          properties: props,
        };
      });
    }

    buildPaymentData(cart) {
      const raw = cart.raw || {};

      const amount = this.money.centsToUnits(raw.original_total_price);

      const hasDiscount = (raw.total_discount || 0) > 0;
      const amountWithDiscount = hasDiscount ? this.money.centsToUnits(raw.total_price) : undefined;
      const discount = hasDiscount ? this.money.centsToUnits(raw.total_discount) : undefined;

      const promocode = Array.isArray(raw.discount_codes) && raw.discount_codes.length
          ? (raw.discount_codes[0]?.code || '')
          : undefined;

      const products = cart.items.map(i => ({
        name: i.title,
        cnt: i.quantity,
        price: this.amountMode === 'cents' ? i.priceCentsEach : this.money.centsToUnits(i.priceCentsEach),
        product_img_src: i.imgURL || '',
        code_product: i.sku && i.sku.length ? i.sku : '0000000000001',
        code_checkbox: Utils.generateUUID(),
      }));

      return Utils.compact({
        amount,
        amount_with_discount: amountWithDiscount,
        discount,
        promocode,
        quantity: cart.totalQuantity,
        products,
      });
    }

    buildUnifiedPayload(cart, phone) {
      const formattedPhone = Utils.formatPhoneE164(phone);
      const phoneNumber = Utils.isValidPhone(formattedPhone) ? formattedPhone : undefined;

      return {
        orderData: {
          lineItems: this.buildLineItems(cart),
          phoneNumber,
        },
        paymentData: this.buildPaymentData(cart),
      };
    }

    async initCheckoutUnified(cart, phone) {
      const payload = this.buildUnifiedPayload(cart, phone);
      const res = await this.api.postJSON(this.endpoints.init, payload);
      if (!res?.success || res?.errors || !res?.data) {
        throw new Error(res?.message || 'Init checkout failed');
      }
      return res.data;
    }

    async run(phone) {
      const cart = await this.parseCart();
      if (!cart.items.length) throw new Error('Cart is empty');

      const redirectUrl = await this.initCheckoutUnified(cart, phone);

      window.location.href = redirectUrl;
    }
  }

  class ButtonLoader {
    constructor(button) {
      this.btn = button;
    }

    ensureLoader() {
      let loader = this.btn.querySelector('.loader');
      if (!loader) {
        loader = document.createElement('div');
        loader.className = 'loader';
        this.btn.appendChild(loader);
      }
      return loader;
    }

    set(isLoading) {
      const text = this.btn.querySelector('.btn-text');
      const icon = this.btn.querySelector('svg');

      if (isLoading) {
        if (text) text.style.display = "none";
        if (icon) icon.style.display = "none";

        this.btn.classList.add('loading');
        this.btn.setAttribute('aria-disabled', 'true');
        this.btn.style.pointerEvents = 'none';
        this.ensureLoader().style.display = 'block';
      } else {
        if (text) text.style.display = "block";
        if (icon) icon.style.display = "block";

        this.btn.classList.remove('loading');
        this.btn.removeAttribute('aria-disabled');
        this.btn.style.pointerEvents = '';
        const loader = this.btn.querySelector('.loader');
        if (loader) loader.style.display = '';
      }
    }
  }

  class App {
    constructor(cfg) {
      this.cfg = cfg;
      this.money = new Money(window.MONEY_FORMAT);
      this.api = new ApiClient(cfg.apiBase, cfg.credentials);
      this.cartApi = new CartApi();
      this.checkout = new CheckoutService({
        money: this.money,
        api: this.api,
        endpoints: cfg.endpoints,
        amountMode: cfg.amountMode,
        cartApi: this.cartApi,
      });
    }

    guardFormFor(btn) {
      const form = btn.closest('form');
      if (!form || form._externalCheckoutGuarded) return;

      form.addEventListener(
          'submit',
          e => {
            const sub = e.submitter || document.activeElement;
            if (sub === btn) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
            }
          },
          true
      );

      form._externalCheckoutGuarded = true;
    }

    bindButton(root = document) {
      const buttons = root.querySelectorAll(this.cfg.checkoutSelector);

      buttons.forEach(btn => {
        if (!btn || btn._externalCheckoutBound) return;

        this.guardFormFor(btn);
        const loader = new ButtonLoader(btn);

        const handler = async e => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          loader.set(true);
          try {
            const phoneInput = document.querySelector('[name="monocheckout_phone"]');
            const phoneValue = phoneInput ? Utils.formatPhoneE164(phoneInput.value) : undefined;
            await this.checkout.run(phoneValue);
          } catch (err) {
            console.error(err);
            loader.set(false);
            alert('Sorry, checkout failed. Please try again.');
          }
        };

        btn.addEventListener('click', handler, { capture: true });

        const guard = ev => {
          if (ev.target === btn) {
            ev.preventDefault();
            ev.stopPropagation();
          }
        };

        btn.addEventListener('mousedown', guard, true);
        btn.addEventListener('touchstart', guard, true);
        btn._externalCheckoutBound = true;
      });
    }

    setupPhoneValidation(root = document) {
      const input = root.querySelector('[name="monocheckout_phone"]');
      if (!input || input._monoPhoneBound) return;

      let touched = false;
      const PREFIX = '+380';

      const enforcePrefixAndDigits = () => {
        let val = String(input.value || '');

        if (!val.startsWith(PREFIX)) {
          val = Utils.formatPhoneE164(val);
        }
        let suffix = val.startsWith(PREFIX) ? val.slice(PREFIX.length).replace(/\D+/g, '') : val.replace(/\D+/g, '');
        // Обмежуємо довжину суфікса 9 цифрами (разом з +380 це 13 символів)
        if (suffix.length > 9) suffix = suffix.slice(0, 9);
        input.value = PREFIX + suffix;
      };

      const updateState = () => {
        const isValid = Utils.isValidPhone(input.value);

        input.classList.toggle('invalid', touched && !isValid);

        if (isValid) {
          root.querySelectorAll(this.cfg.checkoutSelector).forEach(b => b.classList.add('active'));
          root.querySelectorAll('.monocheckout-phone button').forEach(b => b.classList.add('active'));
        } else {
          root.querySelectorAll(this.cfg.checkoutSelector).forEach(b => b.classList.remove('active'));
          root.querySelectorAll('.monocheckout-phone button').forEach(b => b.classList.remove('active'));
        }
      };

      const markTouchedAndUpdate = () => {
        touched = true;
        enforcePrefixAndDigits();
        updateState();
      };

      input.addEventListener('focus', () => {
        if (!String(input.value || '').startsWith(PREFIX)) {
          input.value = PREFIX;
          // Перемістити курсор в кінець
          try {
            const end = input.value.length;
            input.setSelectionRange(end, end);
          } catch {}
        }
        // Забезпечити максимально можливу довжину (+380 + 9 цифр)
        try {
          input.setAttribute('maxlength', '13');
          input.maxLength = 13;
        } catch {}
      });
      input.addEventListener('keydown', (e) => {
        // Забороняємо стирати префікс
        if ((e.key === 'Backspace' || e.key === 'Delete') && input.selectionStart <= PREFIX.length) {
          e.preventDefault();
        }
        // Якщо вже 9 цифр після префікса і немає виділення — блокуємо будь-яке введення цифр
        const val = String(input.value || '');
        const suffix = val.startsWith(PREFIX) ? val.slice(PREFIX.length).replace(/\D+/g, '') : val.replace(/\D+/g, '');
        const selectionCollapsed = input.selectionStart === input.selectionEnd;
        const isDigitKey = e.key.length === 1 && /\d/.test(e.key);
        if (isDigitKey && suffix.length >= 9 && selectionCollapsed && input.selectionStart >= PREFIX.length) {
          e.preventDefault();
        }
      });
      input.addEventListener('input', markTouchedAndUpdate);
      input.addEventListener('change', markTouchedAndUpdate);
      input.addEventListener('blur', markTouchedAndUpdate);

      input._monoPhoneBound = true;
    }

    boot() {
      document.addEventListener('DOMContentLoaded', () => {
        this.bindButton();
        this.setupPhoneValidation();

        const mo = new MutationObserver(() => {
          this.bindButton();
          this.setupPhoneValidation();
        });
        mo.observe(document.documentElement, { childList: true, subtree: true });
      });
    }
  }

  new App(CONFIG).boot();
})();